import { FUNCTION_BASE } from "../lib/constants";
import { authFetch } from "../lib/api";
import React, { useState, useEffect, useRef } from "react";
import { db, auth } from "../lib/firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { FaClipboardList, FaSyncAlt, FaFilter, FaTimes, FaFacebook, FaEllipsisH } from "react-icons/fa";
import "./ManageCampaigns.css";

// ─── helpers ───────────────────────────────────────────────────────────────
const formatBudget = (c) => {
  if (c.daily_budget)    return `$${(parseInt(c.daily_budget) / 100).toFixed(2)}/day`;
  if (c.lifetime_budget) return `$${(parseInt(c.lifetime_budget) / 100).toFixed(2)} total`;
  if (c.budget)          return `$${c.budget}/${c.budgetType === "lifetime" ? "total" : "day"}`;
  return "—";
};

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};

const objectiveLabel = (o) => ({
  OUTCOME_AWARENESS: "Awareness", OUTCOME_TRAFFIC: "Traffic",
  OUTCOME_ENGAGEMENT: "Engagement", OUTCOME_LEADS: "Leads",
  OUTCOME_SALES: "Sales", OUTCOME_APP_PROMOTION: "App Installs",
}[o] || o || "—");

const statusLabel = (item) => {
  // effective_status is Meta's real delivery state — always prefer it
  const eff = item.effective_status || item.effectiveStatus;
  if (eff === "IN_REVIEW" || eff === "PENDING_REVIEW") return "In Review";
  if (eff === "DISAPPROVED" || eff === "REJECTED") return "Rejected";
  if (eff === "CAMPAIGN_PAUSED" || eff === "ADSET_PAUSED") return "Paused (parent)";
  if (eff === "PREAPPROVED") return "Pre-approved";
  if (eff === "WITH_ISSUES") return "Issues";

  const s = item.status || item.fbStatus || eff || "";
  if (s === "ACTIVE" || eff === "ACTIVE") {
    const start = item.startDate || item.start_time;
    if (start && new Date(start) > new Date()) return "Scheduled";
    return "Active";
  }
  if (s === "PAUSED" || eff === "PAUSED") return "Paused";
  const end = item.endDate || item.end_time;
  if (end && new Date(end) < new Date()) return "Ended";
  if (s === "DELETED" || eff === "DELETED") return "Deleted";
  return s || "Unknown";
};

const statusClass = (item) => {
  const s = statusLabel(item);
  if (s === "Active")          return "status-active";
  if (s === "Scheduled")       return "status-scheduled";
  if (s === "In Review")       return "status-review";
  if (s === "Rejected")        return "status-rejected";
  if (s === "Ended" || s === "Deleted") return "status-ended";
  return "status-paused";
};

const Toggle = ({ checked, disabled, onChange }) => (
  <label className="switch">
    <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
    <span className="slider" />
  </label>
);

// ─── Campaign objectives (for filter select) ────────────────────────────────
const campaignObjectives = [
  { id: "OUTCOME_AWARENESS",     label: "Awareness" },
  { id: "OUTCOME_TRAFFIC",       label: "Traffic" },
  { id: "OUTCOME_ENGAGEMENT",    label: "Engagement" },
  { id: "OUTCOME_LEADS",         label: "Leads" },
  { id: "OUTCOME_SALES",         label: "Sales" },
  { id: "OUTCOME_APP_PROMOTION", label: "App Installs" },
];

// ─── ManageCampaigns ────────────────────────────────────────────────────────
const ManageCampaigns = ({ onCreateCampaign }) => {
  const [tab, setTab] = useState("campaigns");
  const [search, setSearch] = useState("");

  // filter state — set when user clicks a row to drill down
  const [filterCampaign, setFilterCampaign] = useState(null); // { id, fbCampaignId, name }
  const [filterAdSet, setFilterAdSet]       = useState(null); // { id, name }

  // data
  const [campaigns, setCampaigns] = useState([]);
  const [adSets, setAdSets]       = useState([]);
  const [ads, setAds]             = useState([]);

  // ui
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingAdSets, setLoadingAdSets]       = useState(false);
  const [loadingAds, setLoadingAds]             = useState(false);
  const [error, setError]                       = useState(null);
  const [togglingId, setTogglingId]             = useState(null);
  const [deletingId, setDeletingId]             = useState(null);
  const [confirmDeleteId, setConfirmDeleteId]   = useState(null);
  const [previewAd, setPreviewAd]               = useState(null);
  const [selectedIds, setSelectedIds]           = useState(new Set());
  const [bulkDeleting, setBulkDeleting]         = useState(false);

  // ─── Filter / Sort / Pagination state ──────────────────────────────────────
  const [statusFilter, setStatusFilter]     = useState("all");
  const [dateFrom, setDateFrom]             = useState("");
  const [dateTo, setDateTo]                 = useState("");
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [enabledFilters, setEnabledFilters] = useState(new Set());
  const [objectiveFilter, setObjectiveFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [sortConfig, setSortConfig]         = useState({ key: null, dir: "asc" });
  const [page, setPage]                     = useState(1);
  const [perPage, setPerPage]               = useState(20);
  const filterPanelRef                      = useRef(null);

  const [fbAccounts, setFbAccounts]         = useState({});
  const [selectedAccount, setSelectedAccount] = useState("");

  // load ad accounts + all data on mount
  useEffect(() => {
    const loadAccounts = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const data = snap.data();
        const accounts = data.fbAccounts || {};
        setFbAccounts(accounts);
        setSelectedAccount(data.selectedAdAccount || Object.keys(accounts)[0] || "");
      }
    };
    loadAccounts();
  }, []);

  // load all three on mount
  useEffect(() => { fetchCampaigns(); fetchAdSets(); fetchAds(); }, []);

  // ─── Click-outside to close filter panel ─────────────────────────────────
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setShowFilterPanel(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // ─── Re-fetch when ad account changes ────────────────────────────────────
  useEffect(() => {
    if (selectedAccount) { fetchCampaigns(selectedAccount); fetchAdSets(); fetchAds(); }
  }, [selectedAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Reset page when filters/search/tab change ───────────────────────────
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, dateFrom, dateTo, objectiveFilter, platformFilter, tab]);

  // ── Fetch all campaigns — Meta is source of truth, merged with Firestore extras ──
  const fetchCampaigns = async (accountId = null) => {
    setLoadingCampaigns(true); setError(null);
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Fetch from Meta and Firestore in parallel
      const acct = accountId || selectedAccount;
      const acctParam = acct ? `?adAccountId=${encodeURIComponent(acct)}` : "";
      const [metaRes, firestoreSnap] = await Promise.all([
        authFetch(`${FUNCTION_BASE}/api/fb/campaigns/${user.uid}${acctParam}`),
        getDocs(query(collection(db, "campaigns"), where("userId", "==", user.uid))),
      ]);

      // Build Firestore lookup by fbCampaignId for extras (platforms, adGroupName, etc.)
      const firestoreMap = {};
      firestoreSnap.docs.forEach(d => {
        const data = d.data();
        if (data.fbCampaignId) firestoreMap[String(data.fbCampaignId)] = { id: d.id, ...data };
      });

      let merged = [];
      if (metaRes.ok) {
        const metaData = await metaRes.json();
        const metaCampaigns = metaData.campaigns || [];
        merged = metaCampaigns.map(mc => {
          const ins = mc.insights?.data?.[0] || {};
          const fs = firestoreMap[String(mc.id)] || {};
          return {
            // Firestore extras (platforms, adGroupName, etc.)
            ...fs,
            // Meta fields take precedence for live data
            fbCampaignId: mc.id,
            name: mc.name,
            objective: mc.objective,
            fbStatus: mc.status,
            status: mc.status,
            effective_status: mc.effective_status,
            daily_budget: mc.daily_budget,
            lifetime_budget: mc.lifetime_budget,
            startDate: mc.start_time || fs.startDate,
            endDate: mc.stop_time || fs.endDate,
            spend: ins.spend || "0",
            impressions: ins.impressions || "0",
            clicks: ins.clicks || "0",
            reach: ins.reach,
            ctr: ins.ctr,
            cpc: ins.cpc,
          };
        });
      } else {
        // Fallback: Firestore only
        merged = firestoreSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      }

      setCampaigns(merged.sort((a, b) => {
        const aT = new Date(a.startDate || 0);
        const bT = new Date(b.startDate || 0);
        return bT - aT;
      }));
    } catch { setError("Failed to load campaigns."); }
    finally { setLoadingCampaigns(false); }
  };

  // ── Fetch ad sets (optionally filtered by campaign) ───────────────────────
  const fetchAdSets = async (campaignId = null, accountId = null) => {
    setLoadingAdSets(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const acct = accountId || selectedAccount;
      const acctParam = acct ? `&adAccountId=${encodeURIComponent(acct)}` : "";
      const url = campaignId
        ? `${FUNCTION_BASE}/api/fb/adsets/${user.uid}?campaignId=${campaignId}${acctParam}`
        : `${FUNCTION_BASE}/api/fb/adsets/${user.uid}?${acctParam.slice(1)}`;
      const res = await authFetch(url);
      const data = await res.json();
      if (data.success) setAdSets(data.adsets);
    } catch {}
    finally { setLoadingAdSets(false); }
  };

  // ── Fetch ads (optionally filtered by ad set) ─────────────────────────────
  const fetchAds = async (adsetId = null, accountId = null) => {
    setLoadingAds(true);
    try {
      const user = auth.currentUser;
      if (!user) return;
      const acct = accountId || selectedAccount;
      const acctParam = acct ? `&adAccountId=${encodeURIComponent(acct)}` : "";
      const url = adsetId
        ? `${FUNCTION_BASE}/api/fb/ads/${user.uid}?adsetId=${adsetId}${acctParam}`
        : `${FUNCTION_BASE}/api/fb/ads/${user.uid}?${acctParam.slice(1)}`;
      const res = await authFetch(url);
      const data = await res.json();
      if (data.success) setAds(data.ads);
    } catch {}
    finally { setLoadingAds(false); }
  };

  // ── Click a campaign row → filter ad sets & ads, switch to ad sets tab ───
  const handleCampaignClick = (c) => {
    setFilterCampaign({ id: c.fbCampaignId, name: c.name });
    setFilterAdSet(null);
    fetchAdSets(c.fbCampaignId);
    fetchAds(); // reset ads to all
    setTab("adsets");
  };

  // ── Click an ad set row → filter ads, switch to ads tab ──────────────────
  const handleAdSetClick = (s) => {
    setFilterAdSet({ id: s.id, name: s.name });
    fetchAds(s.id);
    setTab("ads");
  };

  // ── Switch tab freely ─────────────────────────────────────────────────────
  const switchTab = (t) => {
    setTab(t);
    setSearch("");
    if (t === "campaigns") { setFilterCampaign(null); setFilterAdSet(null); }
    if (t === "adsets")    { setFilterAdSet(null); }
  };

  // ── Thumbnail lookup helpers ──────────────────────────────────────────────
  const getThumb = (ad) => {
    if (!ad?.creative) return null;
    const c = ad.creative;
    return c.image_url
      || c.object_story_spec?.link_data?.picture
      || c.object_story_spec?.link_data?.image_url
      || c.object_story_spec?.video_data?.image_url
      || c.thumbnail_url
      || null;
  };
  const thumbForCampaign = (c) => {
    const campId = String(c.fbCampaignId || c.id || "");
    const ad = ads.find(a => String(a.campaign_id) === campId);
    return getThumb(ad);
  };
  const thumbForAdSet = (s) => {
    const ad = ads.find(a => a.adset_id === s.id || a.adSetId === s.id);
    return getThumb(ad);
  };

  // ── Refresh current tab ───────────────────────────────────────────────────
  const refresh = () => {
    if (tab === "campaigns") fetchCampaigns(selectedAccount);
    if (tab === "adsets")    fetchAdSets(filterCampaign?.id || null);
    if (tab === "ads")       fetchAds(filterAdSet?.id || null);
  };

  // ── Toggle helpers ────────────────────────────────────────────────────────
  const toggleCampaign = async (c) => {
    const next = c.fbStatus === "ACTIVE" ? "PAUSED" : "ACTIVE";
    setTogglingId(c.id);
    try {
      const user = auth.currentUser;
      const res = await authFetch(`${FUNCTION_BASE}/api/campaigns/${c.fbCampaignId}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid, status: next }),
      });
      if (res.ok) setCampaigns(prev => prev.map(x => x.id === c.id ? { ...x, fbStatus: next } : x));
    } catch {}
    setTogglingId(null);
  };

  const toggleAdSet = async (s) => {
    const next = s.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    setTogglingId(s.id);
    try {
      const user = auth.currentUser;
      const res = await authFetch(`${FUNCTION_BASE}/api/adsets/${s.id}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid, status: next }),
      });
      if (res.ok) setAdSets(prev => prev.map(x => x.id === s.id ? { ...x, status: next } : x));
    } catch {}
    setTogglingId(null);
  };

  const toggleAd = async (ad) => {
    const next = ad.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    setTogglingId(ad.id);
    try {
      const user = auth.currentUser;
      const res = await authFetch(`${FUNCTION_BASE}/api/ads/${ad.id}/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid, status: next }),
      });
      if (res.ok) setAds(prev => prev.map(x => x.id === ad.id ? { ...x, status: next } : x));
    } catch {}
    setTogglingId(null);
  };

  const deleteCampaign = async (c) => {
    setDeletingId(c.id);
    setConfirmDeleteId(null);
    try {
      const user = auth.currentUser;
      const res = await authFetch(`${FUNCTION_BASE}/api/campaigns/${c.fbCampaignId}`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid }),
      });
      if (res.ok) setCampaigns(prev => prev.filter(x => x.id !== c.id));
    } catch {}
    setDeletingId(null);
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.size} campaign${selectedIds.size > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    const user = auth.currentUser;
    const toDelete = campaigns.filter(c => selectedIds.has(c.id));
    await Promise.all(toDelete.map(c =>
      authFetch(`${FUNCTION_BASE}/api/campaigns/${c.fbCampaignId}`, {
        method: "DELETE", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.uid }),
      }).catch(() => {})
    ));
    setCampaigns(prev => prev.filter(c => !selectedIds.has(c.id)));
    setSelectedIds(new Set());
    setBulkDeleting(false);
  };

  const handleCopyCampaign = () => {
    const [id] = [...selectedIds];
    const c = campaigns.find(x => x.id === id);
    if (!c) return;
    onCreateCampaign(c);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pagedCampaigns.length && pagedCampaigns.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pagedCampaigns.map(c => c.id)));
    }
  };

  // ─── Filter helpers ───────────────────────────────────────────────────────
  const applyStatus = (item) => {
    if (statusFilter === "all") return true;
    return statusLabel(item).toLowerCase() === statusFilter;
  };

  const applyDate = (item) => {
    if (!dateFrom && !dateTo) return true;
    const raw = item.startDate || item.start_time;
    if (!raw) return true;
    const d = new Date(raw);
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo && d > new Date(dateTo)) return false;
    return true;
  };

  // ─── Toggle optional filter ───────────────────────────────────────────────
  const toggleFilter = (key) => {
    setEnabledFilters(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        if (key === "objective") setObjectiveFilter("");
        if (key === "platform") setPlatformFilter("all");
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // ─── Sort helpers ─────────────────────────────────────────────────────────
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aV = parseFloat(a[sortConfig.key]) || 0;
      const bV = parseFloat(b[sortConfig.key]) || 0;
      return sortConfig.dir === "asc" ? aV - bV : bV - aV;
    });
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon">{sortConfig.dir === "asc" ? "↑" : "↓"}</span>;
  };

  // ─── Filtered data ────────────────────────────────────────────────────────
  const loading = tab === "campaigns" ? loadingCampaigns
                : tab === "adsets"    ? loadingAdSets
                : loadingAds;

  const q = search.toLowerCase();

  const filteredCampaigns = campaigns.filter(c => {
    if (q && !(c.name || "").toLowerCase().includes(q)) return false;
    if (!applyStatus(c)) return false;
    if (!applyDate(c)) return false;
    if (enabledFilters.has("objective") && objectiveFilter && c.objective !== objectiveFilter) return false;
    if (enabledFilters.has("platform") && platformFilter !== "all") {
      if (platformFilter === "facebook" && !c.platforms?.facebook) return false;
      if (platformFilter === "instagram" && !c.platforms?.instagram) return false;
    }
    return true;
  });

  const filteredAdSets = adSets.filter(s => {
    if (q && !(s.name || "").toLowerCase().includes(q)) return false;
    if (!applyStatus(s)) return false;
    if (!applyDate(s)) return false;
    return true;
  });

  const filteredAds = ads.filter(a => {
    if (q && !(a.name || "").toLowerCase().includes(q)) return false;
    if (!applyStatus(a)) return false;
    return true;
  });

  // ─── Sort + paginate ──────────────────────────────────────────────────────
  const paginate = (data) => data.slice((page - 1) * perPage, page * perPage);

  const sortedCampaigns = sortData(filteredCampaigns);
  const sortedAdSets    = sortData(filteredAdSets);
  const sortedAds       = sortData(filteredAds);

  const pagedCampaigns = paginate(sortedCampaigns);
  const pagedAdSets    = paginate(sortedAdSets);
  const pagedAds       = paginate(sortedAds);

  const totalItems = tab === "campaigns" ? filteredCampaigns.length
                   : tab === "adsets"    ? filteredAdSets.length
                   : filteredAds.length;
  const totalPages = Math.ceil(totalItems / perPage);

  const activeCount = totalItems;

  // ─── Pagination footer ────────────────────────────────────────────────────
  const PaginationFooter = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="manage-pagination">
        <div className="manage-pages">
          <button className="manage-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) => typeof p === "string"
              ? <span key={i} className="manage-page-ellipsis">…</span>
              : <button key={p} className={`manage-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
            )}
          <button className="manage-page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
        </div>
      </div>
    );
  };

  return (
    <div className="manage-container">

      {/* Header */}
      <div className="manage-header">
        <div>
          <h1 className="manage-title">Campaigns</h1>
          <p className="manage-subtitle">
            {(filterCampaign || filterAdSet) ? (
              <span className="manage-breadcrumb">
                <span className="manage-bc-item link" onClick={() => { setFilterCampaign(null); setFilterAdSet(null); switchTab("campaigns"); }}>
                  All Campaigns
                </span>
                {filterCampaign && <>
                  <span className="manage-bc-sep"> › </span>
                  <span className={`manage-bc-item${filterAdSet ? " link" : " active"}`}
                    onClick={filterAdSet ? () => { setFilterAdSet(null); fetchAds(); switchTab("adsets"); } : undefined}>
                    {filterCampaign.name}
                  </span>
                </>}
                {filterAdSet && <>
                  <span className="manage-bc-sep"> › </span>
                  <span className="manage-bc-item active">{filterAdSet.name}</span>
                </>}
              </span>
            ) : (
              "Manage your campaigns, ad sets, and ads."
            )}
          </p>
        </div>
      </div>

      {/* Toolbar — Row 1 */}
      <div className="manage-toolbar">
        <button className="manage-create-btn" onClick={onCreateCampaign}>+ Create Campaign</button>
        <input
          className="manage-search"
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {Object.keys(fbAccounts).length > 1 && (
          <select
            className="manage-account-select"
            value={selectedAccount}
            onChange={e => setSelectedAccount(e.target.value)}
          >
            {Object.entries(fbAccounts).map(([id, acc]) => (
              <option key={id} value={id}>{acc.name || id}</option>
            ))}
          </select>
        )}
        <button className="manage-refresh-btn" onClick={refresh}><FaSyncAlt style={{ marginRight: 6 }} />Refresh</button>
        {!loading && <span className="manage-count">{activeCount} result{activeCount !== 1 ? "s" : ""}</span>}
        <div className="manage-perpage">
          Show:
          {[10, 20, 50].map(n => (
            <button key={n} className={`manage-perpage-btn${perPage === n ? " active" : ""}`}
              onClick={() => { setPerPage(n); setPage(1); }}>{n}</button>
          ))}
        </div>
        {selectedIds.size > 0 && (
          <div className="manage-bulk-inline">
            <span className="manage-bulk-count">{selectedIds.size} selected</span>
            <button
              className="manage-bulk-btn manage-bulk-copy"
              onClick={handleCopyCampaign}
              disabled={selectedIds.size !== 1}
              title={selectedIds.size !== 1 ? "Select exactly one to copy" : "Copy campaign"}
            >Copy</button>
            <button
              className="manage-bulk-btn manage-bulk-delete"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
            >{bulkDeleting ? "Deleting..." : "Delete"}</button>
            <button className="manage-bulk-cancel" onClick={() => setSelectedIds(new Set())}>✕</button>
          </div>
        )}
      </div>

      {/* Toolbar — Row 2: Status pills, Date range, Filters button */}
      <div className="manage-toolbar-filters">
        {/* Status pills */}
        <div className="manage-status-pills">
          {["all", "active", "scheduled", "in review", "paused", "rejected", "ended"].map(s => (
            <button
              key={s}
              className={`manage-status-pill${statusFilter === s ? " active" : ""}`}
              onClick={() => setStatusFilter(s)}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>

        <div className="manage-toolbar-sep" />

        {/* Date range */}
        <div className="manage-date-range">
          <span className="manage-date-label">From</span>
          <input
            type="date"
            className="manage-date-input"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
          <span className="manage-date-label">→</span>
          <input
            type="date"
            className="manage-date-input"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
        </div>

        <div className="manage-toolbar-sep" />

        {/* Filters button + dropdown panel */}
        <div className="manage-filter-wrap" ref={filterPanelRef}>
          <button
            className={`manage-filter-btn${showFilterPanel ? " open" : ""}`}
            onClick={() => setShowFilterPanel(v => !v)}
          >
            <FaFilter style={{ fontSize: "0.75rem" }} />
            Filters
          </button>
          {showFilterPanel && (
            <div className="manage-filter-panel">
              <div className="manage-filter-panel-title">Optional Filters</div>
              <label className="manage-filter-option">
                <input
                  type="checkbox"
                  checked={enabledFilters.has("objective")}
                  onChange={() => toggleFilter("objective")}
                />
                Objective
              </label>
              <label className="manage-filter-option">
                <input
                  type="checkbox"
                  checked={enabledFilters.has("platform")}
                  onChange={() => toggleFilter("platform")}
                />
                Platform
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Active filter chips */}
      {(enabledFilters.has("objective") || enabledFilters.has("platform")) && (
        <div className="manage-active-filters">
          {enabledFilters.has("objective") && (
            <div className="manage-filter-chip">
              Objective:
              <select
                value={objectiveFilter}
                onChange={e => setObjectiveFilter(e.target.value)}
              >
                <option value="">All</option>
                {campaignObjectives.map(obj => (
                  <option key={obj.id} value={obj.id}>{obj.label}</option>
                ))}
              </select>
              <button className="manage-chip-remove" onClick={() => toggleFilter("objective")}>×</button>
            </div>
          )}
          {enabledFilters.has("platform") && (
            <div className="manage-filter-chip">
              Platform:
              <div className="manage-chip-pills">
                {["all", "facebook", "instagram"].map(p => (
                  <button
                    key={p}
                    className={`manage-chip-pill${platformFilter === p ? " active" : ""}`}
                    onClick={() => setPlatformFilter(p)}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </button>
                ))}
              </div>
              <button className="manage-chip-remove" onClick={() => toggleFilter("platform")}>×</button>
            </div>
          )}
        </div>
      )}

      {/* Tab nav — below toolbar and filters */}
      <div className="manage-tabs">
        <span className={`manage-tab${tab === "campaigns" ? " active" : ""}`}
          onClick={() => switchTab("campaigns")}>Campaigns</span>
        <span className={`manage-tab${tab === "adsets" ? " active" : ""}`}
          onClick={() => switchTab("adsets")}>Ad Sets</span>
        <span className={`manage-tab${tab === "ads" ? " active" : ""}`}
          onClick={() => switchTab("ads")}>Ads</span>
      </div>

      {loading && <div className="manage-loading">Loading...</div>}
      {error   && <div className="manage-error">{error}</div>}

      {/* ── CAMPAIGNS ── */}
      {!loading && tab === "campaigns" && (
        filteredCampaigns.length === 0 ? (
          <div className="manage-empty">
            <div className="manage-empty-icon"><FaClipboardList /></div>
            <h3>No campaigns yet</h3>
            <p>Campaigns you create will appear here.</p>
          </div>
        ) : (
          <div className="manage-table-card">
            <div className="manage-table-scroll">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th onClick={e => e.stopPropagation()} style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        className="manage-row-checkbox"
                        checked={pagedCampaigns.length > 0 && selectedIds.size === pagedCampaigns.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th></th>
                    <th>Preview</th>
                    <th>Campaign</th>
                    <th>Objective</th>
                    <th
                      className={`th-sortable${sortConfig.key === "budget" ? " sorted" : ""}`}
                      onClick={() => handleSort("budget")}
                    >
                      Budget{sortIcon("budget")}
                    </th>
                    <th>Start</th>
                    <th>End</th>
                    <th>Status</th>
                    <th
                      className={`th-sortable${sortConfig.key === "spend" ? " sorted" : ""}`}
                      onClick={() => handleSort("spend")}
                    >
                      Spend{sortIcon("spend")}
                    </th>
                    <th
                      className={`th-sortable${sortConfig.key === "impressions" ? " sorted" : ""}`}
                      onClick={() => handleSort("impressions")}
                    >
                      Impressions{sortIcon("impressions")}
                    </th>
                    <th
                      className={`th-sortable${sortConfig.key === "clicks" ? " sorted" : ""}`}
                      onClick={() => handleSort("clicks")}
                    >
                      Clicks{sortIcon("clicks")}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCampaigns.map((c) => (
                    <tr key={c.id} className={`manage-row-clickable${selectedIds.has(c.id) ? " manage-row-selected" : ""}`}>
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="manage-row-checkbox"
                          checked={selectedIds.has(c.id)}
                          onChange={() => toggleSelect(c.id)}
                        />
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <Toggle checked={c.fbStatus === "ACTIVE"} disabled={togglingId === c.id} onChange={() => toggleCampaign(c)} />
                      </td>
                      <td onClick={() => handleCampaignClick(c)}>
                        {thumbForCampaign(c)
                          ? <img src={thumbForCampaign(c)} alt="preview" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />
                          : <span className="td-no-preview">—</span>}
                      </td>
                      <td className="td-name" onClick={() => handleCampaignClick(c)}>{c.name || "—"}</td>
                      <td onClick={() => handleCampaignClick(c)}>{objectiveLabel(c.objective)}</td>
                      <td onClick={() => handleCampaignClick(c)}>{formatBudget(c)}</td>
                      <td onClick={() => handleCampaignClick(c)}>{formatDate(c.startDate)}</td>
                      <td onClick={() => handleCampaignClick(c)}>{formatDate(c.endDate)}</td>
                      <td onClick={() => handleCampaignClick(c)}>
                        <span className={`status-badge ${statusClass(c)}`}>{statusLabel(c)}</span>
                      </td>
                      <td onClick={() => handleCampaignClick(c)}>{c.spend ? `$${c.spend}` : "—"}</td>
                      <td onClick={() => handleCampaignClick(c)}>{c.impressions ?? "—"}</td>
                      <td onClick={() => handleCampaignClick(c)}>{c.clicks ?? "—"}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {deletingId === c.id ? (
                          <span className="delete-btn" style={{ opacity: 0.4 }}>Deleting...</span>
                        ) : confirmDeleteId === c.id ? (
                          <div className="delete-confirm-row">
                            <span className="delete-confirm-text">Delete?</span>
                            <button className="delete-confirm-yes" onClick={() => deleteCampaign(c)}>Yes</button>
                            <button className="delete-confirm-no" onClick={() => setConfirmDeleteId(null)}>No</button>
                          </div>
                        ) : (
                          <button className="delete-btn" onClick={() => setConfirmDeleteId(c.id)}>
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationFooter />
          </div>
        )
      )}

      {/* ── AD SETS ── */}
      {!loading && tab === "adsets" && (
        filteredAdSets.length === 0 ? (
          <div className="manage-empty">
            <div className="manage-empty-icon"><FaClipboardList /></div>
            <h3>No ad sets found</h3>
            <p>{filterCampaign ? `No ad sets for "${filterCampaign.name}".` : "No ad sets in this account yet."}</p>
          </div>
        ) : (
          <div className="manage-table-card">
            <div className="manage-table-scroll">
              <table className="manage-table">
                <thead>
                  <tr>{["", "Preview", "Ad Set", "Goal", "Budget", "Start", "End", "Status"].map((h, i) => <th key={i}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {pagedAdSets.map((s) => {
                    const parentCampaign = campaigns.find(c => String(c.fbCampaignId) === String(s.campaign_id));
                    const parentPaused = parentCampaign && parentCampaign.fbStatus !== "ACTIVE";
                    return (
                    <tr key={s.id} className={`manage-row-clickable${parentPaused ? " row-parent-paused" : ""}`}>
                      <td onClick={e => e.stopPropagation()}>
                        <Toggle checked={s.status === "ACTIVE"} disabled={togglingId === s.id} onChange={() => toggleAdSet(s)} />
                      </td>
                      <td onClick={() => handleAdSetClick(s)}>
                        {thumbForAdSet(s)
                          ? <img src={thumbForAdSet(s)} alt="preview" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />
                          : <span className="td-no-preview">—</span>}
                      </td>
                      <td className="td-name" onClick={() => handleAdSetClick(s)}>{s.name || "—"}</td>
                      <td onClick={() => handleAdSetClick(s)}>{s.optimization_goal?.replace(/_/g, " ") || "—"}</td>
                      <td onClick={() => handleAdSetClick(s)}>{formatBudget(s)}</td>
                      <td onClick={() => handleAdSetClick(s)}>{formatDate(s.start_time)}</td>
                      <td onClick={() => handleAdSetClick(s)}>{formatDate(s.end_time)}</td>
                      <td onClick={() => handleAdSetClick(s)}>
                        <span className={`status-badge ${statusClass(s)}`}>{statusLabel(s)}</span>
                        {parentPaused && <span className="status-parent-paused-label" title={`Campaign "${parentCampaign.name}" is paused`}>Campaign off</span>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationFooter />
          </div>
        )
      )}

      {/* ── ADS ── */}
      {!loading && tab === "ads" && (
        filteredAds.length === 0 ? (
          <div className="manage-empty">
            <div className="manage-empty-icon"><FaClipboardList /></div>
            <h3>No ads found</h3>
            <p>{filterAdSet ? `No ads in "${filterAdSet.name}".` : "No ads in this account yet."}</p>
          </div>
        ) : (
          <div className="manage-table-card">
            <div className="manage-table-scroll">
              <table className="manage-table">
                <thead>
                  <tr>{["", "Preview", "Ad Name", "Headline", "Body", "Status"].map((h, i) => <th key={i}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {pagedAds.map((ad) => {
                    const parentAdSet = adSets.find(s => s.id === ad.adset_id);
                    const parentCampaign = parentAdSet
                      ? campaigns.find(c => String(c.fbCampaignId) === String(parentAdSet.campaign_id))
                      : campaigns.find(c => String(c.fbCampaignId) === String(ad.campaign_id));
                    const adSetPaused = parentAdSet && parentAdSet.status !== "ACTIVE";
                    const campaignPaused = parentCampaign && parentCampaign.fbStatus !== "ACTIVE";
                    const parentPaused = adSetPaused || campaignPaused;
                    const parentPausedLabel = campaignPaused ? "Campaign off" : "Ad set off";
                    const parentPausedTitle = campaignPaused
                      ? `Campaign "${parentCampaign.name}" is paused`
                      : `Ad set "${parentAdSet?.name}" is paused`;
                    return (
                    <tr key={ad.id} className={`manage-row-clickable${parentPaused ? " row-parent-paused" : ""}`}>
                      <td onClick={e => e.stopPropagation()}>
                        <Toggle checked={ad.status === "ACTIVE"} disabled={togglingId === ad.id} onChange={() => toggleAd(ad)} />
                      </td>
                      <td onClick={() => setPreviewAd(ad)}>
                        {getThumb(ad)
                          ? <img src={getThumb(ad)} alt="preview" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />
                          : <span className="td-no-preview">—</span>}
                      </td>
                      <td className="td-name" onClick={() => setPreviewAd(ad)}>{ad.name || "—"}</td>
                      <td onClick={() => setPreviewAd(ad)}>{ad.creative?.title || "—"}</td>
                      <td className="td-body" onClick={() => setPreviewAd(ad)}>{ad.creative?.body || "—"}</td>
                      <td onClick={() => setPreviewAd(ad)}>
                        <span className={`status-badge ${statusClass(ad)}`}>{statusLabel(ad)}</span>
                        {parentPaused && <span className="status-parent-paused-label" title={parentPausedTitle}>{parentPausedLabel}</span>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationFooter />
          </div>
        )
      )}

      {/* ── AD PREVIEW MODAL ── */}
      {previewAd && (
        <div className="ad-preview-overlay" onClick={() => setPreviewAd(null)}>
          <div className="ad-preview-modal" onClick={e => e.stopPropagation()}>
            <button className="ad-preview-close" onClick={() => setPreviewAd(null)}><FaTimes /></button>
            <h3 className="ad-preview-title">Ad Preview</h3>
            <p className="ad-preview-name">{previewAd.name}</p>

            {/* Facebook-style ad card */}
            <div className="ad-preview-card">
              {/* Page header */}
              <div className="ad-preview-header">
                <div className="ad-preview-avatar" />
                <div>
                  <div className="ad-preview-page">Your Page</div>
                  <div className="ad-preview-sponsored">Sponsored · <FaFacebook style={{ fontSize: "0.7rem" }} /></div>
                </div>
                <FaEllipsisH className="ad-preview-more" />
              </div>

              {/* Body text */}
              {previewAd.creative?.body && (
                <p className="ad-preview-body">{previewAd.creative.body}</p>
              )}

              {/* Media */}
              {getThumb(previewAd) ? (
                <img src={getThumb(previewAd)} alt="Ad media" className="ad-preview-media" />
              ) : (
                <div className="ad-preview-media-empty">No media available</div>
              )}

              {/* Footer */}
              <div className="ad-preview-footer">
                <div>
                  {previewAd.creative?.title && <div className="ad-preview-headline">{previewAd.creative.title}</div>}
                  {previewAd.creative?.body && <div className="ad-preview-desc">{previewAd.creative.body.slice(0, 60)}{previewAd.creative.body.length > 60 ? "…" : ""}</div>}
                </div>
                <button className="ad-preview-cta">Learn More</button>
              </div>

              {/* Reactions row */}
              <div className="ad-preview-reactions">
                <span>👍 Like</span>
                <span>💬 Comment</span>
                <span>↗ Share</span>
              </div>
            </div>

            <div className="ad-preview-meta">
              <span className={`status-badge ${statusClass(previewAd)}`}>{statusLabel(previewAd)}</span>
              <span className="ad-preview-id">ID: {previewAd.id}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCampaigns;
